import { ListView, TopBarRoute } from "../../../../components/templates";
import { useNavigate, useParams } from "react-router";
import { useDatasetDef } from "../../hooks/useDatasetDefs";
import {
  Button,
  CircularLoader,
  Icon,
  Select,
  Stack,
  Title
} from "@introist/react-foundation/v2";
import { useDataset } from "../../hooks/useDataset";
import { useDatasetFields } from "../../hooks/useDatasetFields";
import { DatasetTable } from "./DatasetTable";
import { DatasetEntryDrawer } from "./DatasetEntryDrawer";
import { DatasetSyncButton } from "../../sync/DatasetSyncButton";
import { useEffect, useMemo, useState } from "react";
import { DatasetSyncDrawer } from "../../sync/SyncDrawer";
import { useLocation } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200];

export const DatasetRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, entryId } = useParams();

  const { def } = useDatasetDef({ id: id! });
  const { fields = [] } = useDatasetFields({ datasetId: def?.id ?? "" }, { enabled: !!def });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[2]);
  useEffect(() => setPage(0), [def?.id]);

  const { entries, total } = useDataset(
    def?.id ?? "",
    { offset: page * pageSize, limit: pageSize },
    { enabled: !!def }
  );

  const pageCount = total ? Math.ceil(total / pageSize) : 0;

  const syncOpen = useMemo(() => {
    return location.pathname.endsWith("sync");
  }, [location]);

  if (!def || !fields) return <CircularLoader fillParent />;

  return (
    <TopBarRoute paths={[{ key: "dataset", title: def.plural }]}>
      <Stack vertical>
        <ListView.Header
          title={def.plural}
          count={total}
          onCreate={() => navigate(`/datasets/${def.id}/new`)}
          right={<DatasetSyncButton datasetId={def.id} />}
        />
        <DatasetTable
          datasetId={def.id}
          entries={entries}
          onClick={id => navigate(`/datasets/${def.id}/${id}`)}
        />
        {pageCount > 0 && (
          <Stack justifyContent="space-between" alignItems="center">
            <Stack gap="small" alignItems="center">
              <Title>Rows per page</Title>
              <Select
                size="small"
                element="button"
                endAdornment={<Icon name="chevronDown" />}
                options={PAGE_SIZE_OPTIONS.map(size => ({ key: String(size), title: String(size) }))}
                value={String(pageSize)}
                onSelect={option => {
                  setPageSize(Number(option.key));
                  setPage(0);
                }}
              />
            </Stack>
            {pageCount > 1 && (
              <Stack gap="medium" alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon="chevronLeft"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  Prev
                </Button>
                <Title>
                  Page {page + 1} of {pageCount}
                </Title>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon="chevronRight"
                  disabled={page + 1 >= pageCount}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
      <DatasetEntryDrawer datasetId={def.id} entryId={entryId} />
      <DatasetSyncDrawer
        datasetId={def.id}
        open={syncOpen}
        onClose={() => navigate("../", { relative: "path" })}
      />
    </TopBarRoute>
  );
};
